import type { IBaseComponentProps } from 'components/base/types'
import type { CSSProperties, FunctionalComponent, MaybeRef } from 'vue'
import type { IForm } from '../types/form'
import type { IconFactor } from '@/types/elements/icon'
import type { FormError, FormValidator } from '../validation'
import type { IFormErrorMessageProps } from '../form-error-message/types'
import type { IBubbletipProps } from 'components/bubbletip/types'
import type { FormFieldRef } from '../types/field'
import type { GridProps } from 'components/grid/types'
import type { Clamp } from '@/types/clamp'
import type { FormFieldInstance, FormFieldOptionsDefinition } from '@/form/useForm'

export interface IInputOption {
  label?: string
  key?: string;
  name?: string
  value?: any;
  selected?: boolean
  bodyLeft?: FunctionalComponent<any>
  bodyRight?: FunctionalComponent<any>
  iconLeft?: IconFactor
  iconRight?: IconFactor
  selectedIcon?: string | FunctionalComponent
  action?: (option: IInputOption) => void
  isPlaceholder?: boolean
  textClass?: string | string[]
  asButton?: boolean
  buttonProps?: any;
  selectedStyle?: string | string[] | CSSProperties | CSSProperties[]
  selectedClass?: string | string[]
  data?: any
  body?: FunctionalComponent<{ isOpen?: MaybeRef<boolean>; }>
  disabled?: boolean
  tooltip?: string
  icon?: IconFactor
  image?: string
}

export type RangeBehaviour = "default" | "rope";
export type RangeKnobBehaviourOptions = {
  pushSiblings?: boolean;
}


export type RangeOptions = Clamp & {
  visualMultiplier?: number;
  toolTip?: Partial<Omit<IBubbletipProps, 'triggerRef' | 'ignore' | 'outputVisibility'>>;
  toolTips?: Partial<Omit<IBubbletipProps, 'triggerRef' | 'ignore' | 'outputVisibility'>>[];
  label?: string;
  labels?: string[];
  unit?: string;
  colors?: string[];
  normalize?: boolean;
  behaviour?: RangeBehaviour;
  knobBehaviour?: RangeKnobBehaviourOptions;
  step?: number;
  ranges?: Omit<RangeOptions, 'ranges'>[];
  appearance?: {
    showSteps?: boolean;
    stepMod?: number;
    knobRailWidth?: number;
  }
}

export interface IInput {
  name?: string
  type: string
  dataRef?: FormFieldInstance;
  componentType?: string
  id?: string
  css?: string | CSSProperties
  reportChange?: (fieldRef: FormFieldRef, nextValue?: any, nullish?: boolean) => void;
  triggerBlur?: (fieldRef: FormFieldRef) => void;
  label?: string | FunctionalComponent<any>
  labelClass?: string
  subLabel?: string
  inlineLabel?: boolean
  labelPlacement?: 'left' | 'right' | 'center' | 'default'
  title?: string
  subtitle?: string
  subtitleClass?: string
  required?: boolean
  validators?: FormValidator<any, any>[]
  errors?: FormError[]
  errorProps?: Partial<IFormErrorMessageProps>
  hidden?: boolean
  value?:any;
  icon?: IconFactor
  placeholder?: any;
  placeholderIcon?: IconFactor
  placeholderClass?: string | string[]
  range?: RangeOptions;
  locked?: boolean
  lockedReason?: string
  selected?: boolean
  disabled?: boolean
  injectedProps?: any
  onInput?: (event: Event) => void
  onBlur?: (event: FocusEvent) => void
  onFocus?: (event: FocusEvent) => void;
  onChange?: (event: Event, meta?: any) => void
  changeReportedCallback?: (field: IInputProps) => void
  focused?: boolean
  touched?: boolean
  numberTweening?: boolean
  fieldRef?: FormFieldRef;
  dependants?: string[]
  clearFieldsIfNotTrue?: string[]
  customComponent?: FunctionalComponent<IInputProps & { state: any }>
  wrapper?: FunctionalComponent<IInputProps>
  after?: FunctionalComponent<any>;
  before?: FunctionalComponent<any>;
  grid?: Omit<GridProps, keyof IBaseComponentProps>;
  numberPrecision?: string;
  wrapperClass?: string | string[]
  tooltip?: string
  bubbleTip?: Omit<IBubbletipProps, 'triggerRef'>;
  key?: string | number
  path?: string
  options?: FormFieldOptionsDefinition;
  display?: {
    value?: string
    title?: string
    body?: FunctionalComponent<any>;
    icon?: IconFactor;
  }
  schema: IForm<any>;
  placement?: 'top' | 'bottom' | 'left' | 'right';
  size?: string;
}

export type IInputProps = IBaseComponentProps & IInput

export const FORM_INPUT_FIELD_PROPS: string[] = [
  'name',
  'onChange',
  'reportChange',
  'triggerBlur',
  'id',
  'placeholder',
  'options',
  'display',
  'required',
  'value',
  'type',
  'locked',
  'onInput',
  'onBlur',
  'onFocus',
  'numberTweening',
  'numberPrecision',
  'fieldRef',
  'dataRef',
  'errors',
  'icon',
  'variant',
  'rounded-sm',
  'before',
  'after',
  'grid',
  'range',
  'schema'
]
